import {computed, shallowRef} from 'vue';
import {
  BF_MODAL_BACKDROP_GAP,
  BF_MODAL_LAYER_STEP
} from '~/utils/bfOverlayZIndex';

type BfModalStackEntry = {
  id: string;
  zIndex: number;
};

const modalStack = shallowRef<BfModalStackEntry[]>([]);
let modalStackId = 0;

const createModalStackId = () => {
  modalStackId += 1;
  return `bf-modal-${modalStackId}`;
};

export const isModalOpen = (): boolean => modalStack.value.length > 0;

const getHighestOpenZIndex = () => {
  return modalStack.value.reduce((highest, entry) => Math.max(highest, entry.zIndex), 0);
};

export const useBfModalStack = (getRequestedZIndex: () => number) => {
  const id = createModalStackId();
  const isRegistered = shallowRef(false);

  const stackEntry = computed(() => {
    return modalStack.value.find(entry => entry.id === id);
  });

  const modalZIndex = computed(() => stackEntry.value?.zIndex ?? getRequestedZIndex());
  const backdropZIndex = computed(() => modalZIndex.value - BF_MODAL_BACKDROP_GAP);
  const hasOpenModals = computed(() => modalStack.value.length > 0);

  const registerModal = () => {
    if (isRegistered.value) return;

    const requestedZIndex = getRequestedZIndex();
    const nextZIndex = modalStack.value.length === 0
      ? requestedZIndex
      : Math.max(requestedZIndex, getHighestOpenZIndex() + BF_MODAL_LAYER_STEP);

    modalStack.value = [
      ...modalStack.value,
      {id, zIndex: nextZIndex}
    ];
    isRegistered.value = true;
  };

  const unregisterModal = () => {
    if (!isRegistered.value) return;

    modalStack.value = modalStack.value.filter(entry => entry.id !== id);
    isRegistered.value = false;
  };

  return {
    backdropZIndex,
    hasOpenModals,
    modalZIndex,
    registerModal,
    unregisterModal
  };
};